/**
 * One filter pill, for every filter on the all-projects board — V7-03 §1, §2.
 *
 * §2 asks to "confirm all filter pills on `/board` share one component and one
 * visual rhythm (the screenshot suggests the project one was bespoke)". They
 * did not: every filter was a bare `<select>`, and the project one was a
 * `multiple` select with `size={1}` — which browsers render as a squat
 * scrolling box that shows one row at a time, cannot be searched, and gives no
 * indication of how many things are selected. That is the control in the
 * founder's screenshot.
 *
 * So the bespoke version is deleted rather than styled into agreement, and this
 * is the one component the whole row is built from. Single-select and
 * multi-select differ only in whether toggling closes the popover.
 *
 * Behaviour is fixed here so it cannot drift per filter:
 *
 *  - **Instant apply.** No Apply button — the board's other filters never had
 *    one, and a filter that needs confirming is a filter people forget to
 *    confirm.
 *  - **Keyboard throughout.** Arrow keys move, Space toggles, Enter toggles and
 *    (single-select) closes, Escape closes and returns focus to the pill.
 *  - **Real button semantics**, with `aria-expanded` and `aria-pressed`, because
 *    a div that opens a menu is invisible to anyone not using a mouse.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface FilterPillOption {
  value: string;
  label: string;
  /** A short badge before the label — a project key, a priority. */
  badge?: string;
  /** Colour for the badge dot. */
  colour?: string;
}

/**
 * What the closed pill reads — §1's rule, extracted so it can be tested
 * without a browser.
 *
 * "All projects" (default), the item's own name when exactly one is selected,
 * or "3 projects" when several are. The singular/plural noun is passed in
 * because "1 projects" is the kind of thing that survives review and then
 * annoys somebody every day.
 */
export function pillLabel(input: {
  selected: string[];
  options: FilterPillOption[];
  emptyLabel: string;
  noun: string;
  nounPlural?: string;
}): string {
  const { selected, options, emptyLabel, noun } = input;
  if (selected.length === 0) return emptyLabel;
  if (selected.length === 1) {
    const match = options.find((option) => option.value === selected[0]);
    // A selection whose option is not in the list — a project the viewer has
    // lost access to — reads as a count rather than as a blank pill.
    if (match) return match.label;
  }
  const plural = input.nounPlural ?? `${noun}s`;
  return `${selected.length} ${selected.length === 1 ? noun : plural}`;
}

export function FilterPill({
  label,
  emptyLabel,
  noun,
  nounPlural,
  options,
  selected,
  onChange,
  multiple = true,
  searchable = true,
  testId,
}: {
  /** The accessible name — "Projects", "Assignee". */
  label: string;
  emptyLabel: string;
  noun: string;
  nounPlural?: string;
  options: FilterPillOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  multiple?: boolean;
  searchable?: boolean;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState(0);
  const pillRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.badge ?? '').toLowerCase().includes(needle),
    );
  }, [options, search]);

  // Rows shift as the search narrows; a cursor left past the end would make
  // Enter do nothing and look broken.
  useEffect(() => {
    setCursor(0);
  }, [search, open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || pillRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function close(): void {
    setOpen(false);
    setSearch('');
    pillRef.current?.focus();
  }

  function toggle(value: string): void {
    if (!multiple) {
      onChange(selected[0] === value ? [] : [value]);
      close();
      return;
    }
    onChange(
      selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value],
    );
  }

  /** `[]` — the "All …" row, which is a clearing action rather than an option. */
  function selectAll(): void {
    onChange([]);
    if (!multiple) close();
  }

  const text = pillLabel({ selected, options, emptyLabel, noun, nounPlural });

  return (
    <div className="relative">
      <button
        ref={pillRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-pressed={selected.length > 0}
        aria-label={label}
        data-testid={testId}
        data-selected={selected.length}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          'flex h-7 items-center gap-1 rounded-full border px-2.5 text-[12px] transition-colors duration-[120ms]',
          selected.length > 0
            ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--text)]'
            : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-2)]',
        )}
      >
        <span className="max-w-[160px] truncate">{text}</span>
        <ChevronDown className="size-3 shrink-0" aria-hidden />
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple}
          data-testid={`${testId}-popover`}
          className="vt-surface vt-elevated absolute z-40 mt-1 flex max-h-[320px] w-[260px] flex-col gap-1 overflow-y-auto p-1.5"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              close();
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setCursor((c) => Math.min(c + 1, visible.length));
              return;
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
              return;
            }
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              // Row 0 is always the "All …" row.
              if (cursor === 0) selectAll();
              else if (visible[cursor - 1]) toggle(visible[cursor - 1].value);
            }
          }}
        >
          {searchable && (
            <Input
              autoFocus
              className="h-7 text-[12px]"
              aria-label={`Search ${label.toLowerCase()}`}
              data-testid={`${testId}-search`}
              placeholder="Search…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          )}

          <button
            type="button"
            role="option"
            aria-selected={selected.length === 0}
            data-testid={`${testId}-all`}
            data-cursor={cursor === 0 ? 'true' : undefined}
            onClick={selectAll}
            className={cn(
              'flex items-center gap-2 rounded-[6px] px-1.5 py-1 text-left text-[12px]',
              cursor === 0 && 'bg-[var(--surface-2)]',
            )}
          >
            <span className="grid size-3.5 place-items-center">
              {selected.length === 0 && <Check className="size-3 text-[var(--accent)]" />}
            </span>
            {emptyLabel}
          </button>

          {visible.map((option, index) => {
            const on = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={on}
                data-testid={`${testId}-option`}
                data-value={option.value}
                data-cursor={cursor === index + 1 ? 'true' : undefined}
                onClick={() => toggle(option.value)}
                className={cn(
                  'flex items-center gap-2 rounded-[6px] px-1.5 py-1 text-left text-[12px]',
                  cursor === index + 1 && 'bg-[var(--surface-2)]',
                )}
              >
                <span className="grid size-3.5 place-items-center">
                  {on && <Check className="size-3 text-[var(--accent)]" />}
                </span>
                {option.badge && (
                  <span
                    className="vt-mono rounded-[4px] px-1 text-[10px]"
                    style={{
                      background: 'var(--surface-2)',
                      color: option.colour || 'var(--text-muted)',
                    }}
                  >
                    {option.badge}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
              </button>
            );
          })}

          {visible.length === 0 && (
            <p className="px-1.5 py-1 text-[12px] vt-muted" data-testid={`${testId}-empty`}>
              Nothing matches “{search.trim()}”.
            </p>
          )}

          {selected.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="justify-start"
              data-testid={`${testId}-clear`}
              onClick={selectAll}
            >
              <X className="size-3" />
              Clear
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
